import React from "react";
import { PanelRightClose, PanelRightOpen } from "lucide-react";
import { useLocation } from "react-router";

import type { AuthContextType } from "../../auth/AuthContext";
import { getProcessLogReactKey, useItemContext } from "../../items/model/ItemContext";
import ItemEditor from "../../items/components/ItemEditor";
import { getVisibleProcessLogs } from "../process-records.utils";
import type { ProcessLogFilter, ProcessLogMessage } from "../process-records.utils";
import IsRunningSwitch from "./IsRunningSwitch";
import SideBarLogItem from "./SideBarLogItem";

interface ProcessLogSidebarProps {
  auth: AuthContextType;
  processLogs: ProcessLogMessage[];
  collapsed: boolean;
  onCollapsedChange: (collapsed: boolean) => void;
  isUserRunning: boolean;
  onIsUserRunningChange: (running: boolean) => void;
  error: string;
  onErrorMessage: (message: string) => void;
}

const ProcessLogSidebar = ({
  auth, processLogs, collapsed, onCollapsedChange, isUserRunning,
  onIsUserRunningChange, error, onErrorMessage,
}: ProcessLogSidebarProps) => {
  const [filter, setFilter] = React.useState<ProcessLogFilter>("all");
  const [editingItem, setEditingItem] = React.useState<{ id: string; duplicated?: boolean } | null>(null);
  const location = useLocation();
  const { fetchItems } = useItemContext();
  const [isInitialRender, setIsInitialRender] = React.useState(true);
  const visibleLogs = React.useMemo(
    () => getVisibleProcessLogs(processLogs, filter),
    [processLogs, filter],
  );
  const failedCount = React.useMemo(
    () => getVisibleProcessLogs(processLogs, "failed").length,
    [processLogs],
  );

  React.useEffect(() => {
    const timer = window.setTimeout(() => setIsInitialRender(false), 100);
    return () => window.clearTimeout(timer);
  }, []);

  const openItemEdit = (event: React.MouseEvent<HTMLAnchorElement>, itemId: string) => {
    if (event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
    event.preventDefault();
    setEditingItem({ id: itemId });
  };

  const refreshList = () => {
    if (location.pathname !== "/" && location.pathname !== "/items/") return;
    const search = new URLSearchParams(location.search);
    const params = {
      compare_site: search.get("compare_site") || undefined,
      q: search.get("q") || undefined,
    };
    fetchItems(auth, () => {}, params);
  };

  return (<>
    <aside className={`sidebar-fixed ${collapsed ? "collapsed" : ""} ${isInitialRender ? "no-transition" : ""}`}>
      <div className="sidebar-header">
        <button
          type="button"
          className="sidebar-toggle"
          onClick={() => onCollapsedChange(!collapsed)}
          title={collapsed ? "작업이력 펼치기" : "작업이력 접기"}
          aria-label={collapsed ? "작업이력 펼치기" : "작업이력 접기"}
        >
          {collapsed ? <PanelRightOpen aria-hidden="true" /> : <PanelRightClose aria-hidden="true" />}
        </button>
        <div className="sidebar-header-content">
          <h2>작업이력</h2>
          <IsRunningSwitch
            auth={auth}
            setErrorMessage={onErrorMessage}
            isUserRunning={isUserRunning}
            onIsUserRunningChange={onIsUserRunningChange}
          />
        </div>
      </div>
      <div className="item-list-status-filter sidebar-process-log-filter">
        <div className="item-list-status-filter-options" role="group" aria-label="작업이력 상태">
          <button
            type="button"
            className={`item-list-status-filter-button ${filter === "all" ? "active" : ""}`}
            aria-pressed={filter === "all"}
            onClick={() => setFilter("all")}
          >
            전체
          </button>
          <button
            type="button"
            className={`item-list-status-filter-button ${filter === "failed" ? "active" : ""}`}
            aria-pressed={filter === "failed"}
            aria-label={`실패 ${failedCount}건`}
            onClick={() => setFilter("failed")}
          >
            실패({failedCount})
          </button>
        </div>
      </div>
      {error && !collapsed && (
        <div className="sidebar-process-log-error" role="alert">{error}</div>
      )}
      <div className="sidebar-log-list">
        {visibleLogs.length > 0 ? (
          visibleLogs.map(({ message, originalIndex }) => (
            <SideBarLogItem
              key={getProcessLogReactKey(message)}
              message={message}
              index={originalIndex}
              collapsed={collapsed}
              onOpenItemEdit={openItemEdit}
            />
          ))
        ) : filter === "failed" && !collapsed ? (
          <div className="sidebar-process-log-empty">실패한 작업이 없습니다.</div>
        ) : null}
      </div>
    </aside>
    {editingItem && (
      <ItemEditor
        key={editingItem.id}
        itemId={editingItem.id}
        duplicated={editingItem.duplicated}
        onClose={() => setEditingItem(null)}
        onChanged={() => { setEditingItem(null); refreshList(); }}
        onDuplicated={(id) => { refreshList(); setEditingItem({ id, duplicated: true }); }}
        onOpenItem={(id) => setEditingItem({ id })}
      />
    )}
  </>);
};

export default ProcessLogSidebar;
